/**
 * Biedradar Pro — shared domain types for the advisor deal-flow workspace.
 *
 * These are the contract between the Pro SPA (`@biedradar/pro-client`) and the
 * Pro API module in the server. They're kept separate from the consumer report
 * types in `../index.ts` so neither bundle pulls in the other. Import via
 * `@biedradar/shared/pro`.
 *
 * Monetary values are integers in euros (matching the rest of the codebase).
 */

export type ID = string;
export type ISODateString = string;

/** Membership roles inside an organization. `client` is the portal role. */
export type Role = "admin" | "advisor" | "assistant" | "client";

export type DealStatus = "active" | "won" | "lost" | "on_hold";

export type StepStatus = "not_started" | "in_progress" | "blocked" | "done";

export type BidStatus =
  | "draft"
  | "submitted"
  | "accepted"
  | "rejected"
  | "withdrawn";

/** Controls what the read-only client portal renders. */
export type Visibility = "internal" | "shared";

/** Premade step types; `custom` is the advisor-defined catch-all (§6). */
export type StepTypeKey =
  | "property"
  | "report"
  | "offer"
  | "offer_accepted"
  | "appraisal"
  | "mortgage"
  | "notary"
  | "sign"
  | "document_request"
  | "financials"
  | "custom";

export interface OrgBranding {
  logoUrl?: string | null;
  accentColor?: string | null;
}

/**
 * Extended organization profile — business/contact details and client-portal
 * preferences. Stored as a JSON blob on the org (additive); all fields optional
 * so a workspace works fully without filling any of it in.
 */
export interface OrgProfile {
  /** Public-facing contact email for the business. */
  contactEmail?: string | null;
  phone?: string | null;
  website?: string | null;
  /** Dutch Chamber of Commerce (KvK) number. */
  kvk?: string | null;
  /** VAT / BTW number. */
  vat?: string | null;
  addressLine?: string | null;
  postalCode?: string | null;
  city?: string | null;
  /** Support email shown to clients in the portal. */
  supportEmail?: string | null;
  /** Optional welcome message shown to clients in their portal space. */
  portalWelcome?: string | null;
}

export interface Organization {
  id: ID;
  name: string;
  branding?: OrgBranding | null;
  profile?: OrgProfile | null;
  plan?: string | null;
  /** True once anyone in the workspace has discovered the deal view-toggle
   *  (flow ↔ board); gates the one-time discovery hint. */
  viewHintDismissed?: boolean;
  createdAt: ISODateString;
}

export interface Membership {
  id: ID;
  organizationId: ID;
  userId: ID;
  role: Role;
  name: string;
  email: string;
}

export type MemberInviteStatus = "pending" | "accepted" | "revoked";

/** A pending invite for a teammate to join an organization at a given role. */
export interface MemberInvite {
  id: ID;
  organizationId: ID;
  email: string;
  role: Role;
  status: MemberInviteStatus;
  createdAt: ISODateString;
}

/** Authenticated advisor identity from better-auth (subset of the user row). */
export interface ProUser {
  id: ID;
  email: string;
  name: string;
  emailVerified: boolean;
  image?: string | null;
}

/**
 * Response of `GET /api/pro/me`. `user` is null for an anonymous request (demo
 * mode). `memberships` lists every org the user belongs to; `activeOrg` is the
 * resolved acting org (the one `proContext` scopes Pro queries to) — present
 * once provisioning has run, or when the demo fallback supplies one.
 */
export interface ProMe {
  user: ProUser | null;
  memberships: Membership[];
  activeOrg: Organization | null;
}

export interface Client {
  id: ID;
  organizationId: ID;
  name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  createdAt: ISODateString;
}

export interface Deal {
  id: ID;
  organizationId: ID;
  clientId: ID;
  assignedUserId?: ID | null;
  title: string;
  description?: string | null;
  status: DealStatus;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export interface Position {
  x: number;
  y: number;
}

export interface Step {
  id: ID;
  dealId: ID;
  typeKey: StepTypeKey;
  title: string;
  status: StepStatus;
  position: Position;
  /** Type-specific structured fields (the "records"). */
  records: Record<string, unknown>;
  visibility: Visibility;
}

export interface StepEdge {
  id: ID;
  dealId: ID;
  sourceStepId: ID;
  targetStepId: ID;
  label?: string | null;
}

export interface Property {
  id: ID;
  dealId: ID;
  address: string;
  postcode?: string | null;
  /** euros, integer */
  askingPrice?: number | null;
  /** euros, integer — from the engine or advisor */
  estimatedValue?: number | null;
  reportId?: ID | null;
  status?: string | null;
  /** WGS84 latitude (from the address geocoder), for the property map. */
  lat?: number | null;
  /** WGS84 longitude. */
  lng?: number | null;
}

/**
 * A selling agent (makelaar) on a deal. A deal can have several; viewed
 * properties link to one, and one is the deal's primary (the main contact).
 * Reusable record so the same agent links across the deal's properties.
 */
export interface Agent {
  id: ID;
  dealId: ID;
  name: string;
  agency: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  createdAt: ISODateString;
}

/** A property (a property-&-viewing step) with its client/deal context, for the
 *  /Properties page. */
export interface PropertyListing {
  stepId: ID;
  dealId: ID;
  dealStatus: DealStatus;
  stepStatus: StepStatus;
  clientId: ID;
  clientName: string;
  address: string;
  postcode: string | null;
  viewed: boolean;
  viewingDate: ISODateString | null;
  /** The linked selling agent's display name, if one is set on the step. */
  agentName: string | null;
}

/** A selling agent with its deal/client/property context, for the /Sellers page. */
export interface AgentListing {
  agent: Agent;
  dealId: ID;
  dealTitle: string;
  dealStatus: DealStatus;
  clientId: ID;
  clientName: string;
  address: string;
}

/** Where a viewed property sits in the client's search. */
export type ViewingStatus = "scheduled" | "viewed" | "interested" | "rejected";
export const VIEWING_STATUSES: readonly ViewingStatus[] = ["scheduled", "viewed", "interested", "rejected"];

/**
 * A property the client viewed (or is scheduled to view) as part of a deal's
 * search. A deal has one target Property (the one being bought) plus any number
 * of these — the "client viewing many properties" list on the property step.
 */
export interface Viewing {
  id: ID;
  dealId: ID;
  address: string;
  postcode: string | null;
  lat: number | null;
  lng: number | null;
  /** ISO date of the viewing (date-only is fine), or null if not scheduled. */
  viewingDate: ISODateString | null;
  notes: string | null;
  status: ViewingStatus;
  /** Free-text selling agent (legacy / fallback when no structured agent is linked). */
  agent: string | null;
  /** Structured link to one of the deal's selling agents (`Agent.id`), or null. */
  agentId: ID | null;
  createdAt: ISODateString;
}

/** One property step's viewing (viewed? + date) with its deal/client context,
 *  for the cross-deal Agenda. */
export interface AgendaEntry {
  dealId: ID;
  stepId: ID;
  clientName: string;
  dealTitle: string;
  address: string;
  viewingDate: ISODateString | null;
  viewed: boolean;
}

/** One address-autocomplete suggestion (provider-agnostic; Google or PDOK). */
export interface AddressSuggestion {
  /** Provider-specific id used to resolve the full address. */
  id: string;
  /** Human-readable line shown in the dropdown. */
  label: string;
}

/** A resolved address with coordinates, normalized across providers. */
export interface ResolvedAddress {
  address: string;
  postcode: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  label: string;
}

export interface Bid {
  id: ID;
  propertyId: ID;
  /** euros, integer */
  amount: number;
  /** voorbehouden — conditions on the offer */
  conditions: string[];
  bidDate?: ISODateString | null;
  status: BidStatus;
}

export interface SellerContact {
  id: ID;
  propertyId: ID;
  name?: string | null;
  agency?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

/**
 * Financing-gap snapshot (§10). Dutch rule: the mortgage is on the lower of
 * purchase price and appraised/estimated value, up to the LTV cap; the rest is
 * own money. Computed off the shared engine, never re-derived in the UI.
 */
export interface FinancingGap {
  /** euros, integer */
  askingPrice: number;
  bid?: number | null;
  estimatedValue?: number | null;
  maxMortgage?: number | null;
  /** max(0, bid − min(estimatedValue, bid)) — own-funds shortfall */
  ownFundsGap: number;
  /** The price the gap is computed against (accepted/latest bid, else asking). */
  purchasePrice?: number;
  /** Own funds applied to the purchase (from ClientFinancials.ownFunds). */
  ownFunds?: number;
  /** The bank's income-based max mortgage for this deal, if a MortgageProfile exists. */
  bankMaxMortgage?: number | null;
}

/** Dutch mortgage repayment forms. */
export type MortgageType = "annuity" | "linear" | "interest_only";
export const MORTGAGE_TYPES: readonly MortgageType[] = ["annuity", "linear", "interest_only"];

/**
 * A client's financial position — the inputs that size the mortgage and the
 * own-funds gap. One row per client (the advisor edits it from the deal). All
 * amounts are integer euros; the two *MonthlyIncome/obligations are euros/month.
 */
export interface ClientFinancials {
  clientId: ID;
  /** Gross annual income of the primary applicant. */
  grossAnnualIncome: number | null;
  /** Gross annual income of a co-applicant / partner. */
  partnerAnnualIncome: number | null;
  /** Liquid own funds available (spaargeld / eigen geld). */
  ownFunds: number | null;
  /** Other structural income, euros/month (e.g. rental). */
  otherMonthlyIncome: number | null;
  /** Monthly debt obligations, euros/month (loans, alimony). */
  monthlyObligations: number | null;
  /** Outstanding student debt (studieschuld) — lowers borrowing capacity. */
  studentDebt: number | null;
  updatedAt: ISODateString | null;
}

/** A mortgage offer / scenario for a specific deal. */
export interface MortgageProfile {
  dealId: ID;
  bankName: string | null;
  /** Max mortgage the bank will provide for this purchase, integer euros. */
  maxMortgage: number | null;
  /** Annual nominal interest rate in basis points (365 = 3.65%) — integer. */
  interestRateBps: number | null;
  /** Term in years (looptijd). */
  termYears: number | null;
  mortgageType: MortgageType | null;
  /** Nationale Hypotheek Garantie applies. */
  nhg: boolean;
  updatedAt: ISODateString | null;
}

// ── API aggregate shapes (reused FE + BE) ────────────────────────────────────

/**
 * The advisor-facing "what's next" for a deal, as **structured data** so the FE
 * can localize it. The server never ships a pre-formatted sentence; it ships the
 * kind plus the step it refers to. The client renders a localized label from
 * `stepTypeKey` (canonical stage name) for known types, falling back to the
 * user-edited `stepTitle` for `custom` steps.
 */
export type NextActionKind = "won" | "ready" | "blocked" | "in_progress" | "next";

export interface NextAction {
  kind: NextActionKind;
  /** The step type this refers to; `null` for `won`/`ready` (no specific step). */
  stepTypeKey: StepTypeKey | null;
  /** The step's title (used verbatim for `custom` steps / as a fallback). */
  stepTitle: string | null;
}

/** Row in the portfolio / client list (FR-1). */
export interface DealSummary {
  deal: Deal;
  client: Client;
  property: Property | null;
  assignee: Pick<Membership, "userId" | "name" | "email"> | null;
  stepCounts: { total: number; done: number; blocked: number };
  financingGap: FinancingGap | null;
  nextAction: NextAction | null;
  lastActivityAt: ISODateString;
}

/** Everything needed to render one deal's canvas + side panels. */
export interface DealDetail {
  deal: Deal;
  client: Client;
  property: Property | null;
  bids: Bid[];
  seller: SellerContact | null;
  steps: Step[];
  edges: StepEdge[];
  financingGap: FinancingGap | null;
  /** The client's financial profile (income + own funds), if entered. */
  financials: ClientFinancials | null;
  /** The deal's mortgage offer (bank / max / rate), if entered. */
  mortgage: MortgageProfile | null;
  /** Properties the client viewed during the search (property step). */
  viewings: Viewing[];
  /** The deal's selling agents (makelaars); one may be primary. */
  agents: Agent[];
  documents: DealDocument[];
  comments: Comment[];
  signingRequests: SigningRequest[];
}

// ── Catalog metadata + defaults (founder-edited constants, §6) ───────────────

export interface StepTypeMeta {
  key: StepTypeKey;
  label: string;
  /** lucide-react icon name, resolved in the client registry. */
  icon: string;
  description: string;
}

export const STEP_TYPES: readonly StepTypeMeta[] = [
  { key: "property", label: "Property & viewing", icon: "Home", description: "The target home and viewings" },
  { key: "report", label: "Biedradar report", icon: "Radar", description: "Pull the data report into the deal" },
  { key: "offer", label: "Offer / bid", icon: "Gavel", description: "Track the offer and its conditions" },
  { key: "offer_accepted", label: "Offer accepted", icon: "BadgeCheck", description: "Acceptance milestone" },
  { key: "appraisal", label: "Appraisal (taxatie)", icon: "ClipboardCheck", description: "NWWI appraisal and value" },
  { key: "mortgage", label: "Mortgage / bank", icon: "Landmark", description: "Mortgage application milestones" },
  { key: "notary", label: "Notary / passing", icon: "ScrollText", description: "Closing at the notary" },
  { key: "sign", label: "Sign document", icon: "PenLine", description: "Send a document out to be signed" },
  { key: "document_request", label: "Document request", icon: "FileUp", description: "Ask the client for files" },
  { key: "financials", label: "Financials", icon: "Calculator", description: "Affordability snapshot and the gap" },
  { key: "custom", label: "Custom step", icon: "Shapes", description: "Anything you define" },
];

export const STEP_TYPE_BY_KEY: Record<StepTypeKey, StepTypeMeta> =
  Object.fromEntries(STEP_TYPES.map((t) => [t.key, t])) as Record<
    StepTypeKey,
    StepTypeMeta
  >;

export const STEP_STATUSES: readonly StepStatus[] = [
  "not_started",
  "in_progress",
  "blocked",
  "done",
];

export const DEAL_STATUSES: readonly DealStatus[] = [
  "active",
  "won",
  "lost",
  "on_hold",
];

export interface DealTemplateStep {
  typeKey: StepTypeKey;
  title: string;
}

/** A selectable starting pipeline for a new deal (§6). */
export type DealTemplateKey = "buy" | "mortgage" | "sell" | "refinance";

export interface DealTemplate {
  key: DealTemplateKey;
  /** Display name — Dutch canonical; the FE localizes via `templates.<key>.*`. */
  name: string;
  description: string;
  /** lucide-react icon name for the picker. */
  icon: string;
  steps: readonly DealTemplateStep[];
}

/**
 * The standard Dutch buyer journey, seeded into each new deal (§6, decided).
 * Founder-edited here; on seed it is copied into the org's own rows so later
 * product work can expose per-org customization without touching this constant.
 * Titles are **Dutch by default** (the product is Dutch-first); a step title is
 * free text the advisor can rename — it's data, not a localized UI label.
 */
export const DEFAULT_DEAL_TEMPLATE: readonly DealTemplateStep[] = [
  { typeKey: "property", title: "Woning & bezichtiging" },
  { typeKey: "offer", title: "Bod" },
  { typeKey: "offer_accepted", title: "Bod geaccepteerd" },
  { typeKey: "report", title: "Biedradar-rapport" },
  { typeKey: "appraisal", title: "Taxatie" },
  { typeKey: "mortgage", title: "Hypotheek / bank" },
  { typeKey: "notary", title: "Notaris / passeren" },
];

/**
 * The four starter pipelines an advisor can pick from when creating a deal.
 * `buy` reuses DEFAULT_DEAL_TEMPLATE so the historical default is unchanged.
 * Step titles are Dutch-first data (seeded verbatim, then renameable).
 */
export const DEAL_TEMPLATES: readonly DealTemplate[] = [
  {
    key: "buy",
    name: "Aankoop",
    icon: "Home",
    description: "De standaard kopersreis: van bezichtiging tot sleuteloverdracht.",
    steps: DEFAULT_DEAL_TEMPLATE,
  },
  {
    key: "mortgage",
    name: "Hypotheekadvies",
    icon: "Landmark",
    description: "Adviestraject: intake, aanvraag, taxatie en passeren.",
    steps: [
      { typeKey: "financials", title: "Intake & financiële positie" },
      { typeKey: "document_request", title: "Documenten opvragen" },
      { typeKey: "mortgage", title: "Hypotheekaanvraag" },
      { typeKey: "appraisal", title: "Taxatie" },
      { typeKey: "sign", title: "Bindend aanbod tekenen" },
      { typeKey: "notary", title: "Notaris / passeren" },
    ],
  },
  {
    key: "sell",
    name: "Verkoop",
    icon: "Tag",
    description: "Verkooptraject: van opname tot overdracht bij de notaris.",
    steps: [
      { typeKey: "property", title: "Opname & waardebepaling" },
      { typeKey: "document_request", title: "Verkoopdossier" },
      { typeKey: "custom", title: "Te koop & bezichtigingen" },
      { typeKey: "offer", title: "Bod & onderhandeling" },
      { typeKey: "offer_accepted", title: "Bod geaccepteerd" },
      { typeKey: "sign", title: "Koopovereenkomst tekenen" },
      { typeKey: "notary", title: "Notaris / overdracht" },
    ],
  },
  {
    key: "refinance",
    name: "Oversluiten",
    icon: "RefreshCw",
    description: "Bestaande hypotheek oversluiten naar betere voorwaarden.",
    steps: [
      { typeKey: "financials", title: "Intake & huidige situatie" },
      { typeKey: "document_request", title: "Documenten opvragen" },
      { typeKey: "appraisal", title: "Taxatie" },
      { typeKey: "mortgage", title: "Nieuwe hypotheek" },
      { typeKey: "sign", title: "Offerte tekenen" },
      { typeKey: "notary", title: "Notaris / passeren" },
    ],
  },
];

export const DEFAULT_TEMPLATE_KEY: DealTemplateKey = "buy";

/** Resolve a template by key, falling back to the default (buy) for unknowns. */
export function dealTemplateByKey(key: string | null | undefined): DealTemplate {
  return DEAL_TEMPLATES.find((tpl) => tpl.key === key) ?? DEAL_TEMPLATES[0]!;
}

/**
 * An org-scoped pipeline the advisor saved themselves. Sits alongside the
 * built-in `DEAL_TEMPLATES` in the new-deal picker; when none exist the deal
 * falls back to the default journey, exactly as before.
 */
export interface CustomDealTemplate {
  id: ID;
  name: string;
  steps: DealTemplateStep[];
  createdAt: ISODateString;
}

/** Payload to save a custom template (name + an ordered list of step types). */
export interface CustomDealTemplateInput {
  name: string;
  steps: DealTemplateStep[];
}

/** Canvas layout constants shared by the seeder and the client. The deal graph
 *  flows top-to-bottom: steps stack in a single vertical lane (`laneX`), each
 *  the next `stepHeight + stepGapY` below the last. `stepGapX` still spaces a
 *  branch dragged off to the side. */
export const CANVAS = {
  stepWidth: 248,
  stepHeight: 110,
  stepGapX: 88,
  stepGapY: 30,
  /** X of the main vertical lane. */
  laneX: 120,
  /** Y of the first step (and legacy single-row baseline). */
  laneY: 64,
} as const;

// ── Collaboration: documents, comments, e-sign (§9, §8) ─────────────────────

export type AuthorType = "user" | "client";

export type DocumentStatus = "requested" | "received" | "in_review" | "approved";

export const DOCUMENT_STATUSES: readonly DocumentStatus[] = [
  "requested",
  "received",
  "in_review",
  "approved",
];

export interface DealDocument {
  id: ID;
  dealId: ID;
  stepId: ID | null;
  name: string;
  mime: string | null;
  /** bytes */
  size: number | null;
  status: DocumentStatus;
  uploadedByType: AuthorType | null;
  uploadedById: ID | null;
  uploadedByName: string | null;
  visibility: Visibility;
  /** false for a pure "please upload X" request with no file yet */
  hasFile: boolean;
  /** Latest e-sign status for this document, or null when no signature was asked. */
  signingStatus?: SigningStatus | null;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export interface Comment {
  id: ID;
  dealId: ID;
  stepId: ID | null;
  authorType: AuthorType;
  authorId: ID | null;
  authorName: string;
  body: string;
  createdAt: ISODateString;
}

export type SigningStatus = "draft" | "sent" | "viewed" | "signed" | "expired";

/**
 * Where the signature goes on the document (DocuSign-style placement). All
 * coordinates are fractions of the page box (0..1, origin top-left); `page` is
 * 1-indexed. `null` on a request means no explicit placement — the signer just
 * signs in the panel.
 */
export interface SignatureField {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SigningRequest {
  id: ID;
  dealId: ID;
  stepId: ID | null;
  documentId: ID | null;
  title: string;
  signerName: string;
  signerEmail: string | null;
  status: SigningStatus;
  /** "internal" for the in-house tier; "eidas:<name>" for a bound provider (§9). */
  provider: string;
  /** Advisor-placed signature box on the document, if any. */
  signatureField?: SignatureField | null;
  createdAt: ISODateString;
  signedAt: ISODateString | null;
}

// ── Client portal ("the space") — read-only, shared data only (§8) ───────────

export interface PortalStep {
  id: ID;
  typeKey: StepTypeKey;
  title: string;
  status: StepStatus;
}

export interface PortalDeal {
  dealId: ID;
  orgName: string;
  clientName: string;
  dealTitle: string;
  propertyAddress: string | null;
  steps: PortalStep[];
  documents: DealDocument[];
  comments: Comment[];
  signingRequests: SigningRequest[];
}

/** One deal a logged-in client has been granted access to (the space picker). */
export interface PortalGrantSummary {
  dealId: ID;
  orgName: string;
  dealTitle: string;
  propertyAddress: string | null;
}

/**
 * Response of `GET /api/portal/me` — the durable client-login identity. `user`
 * is null when there's no better-auth session; `deals` lists every deal the
 * client holds a `pro_portal_grants` row for (their accessible spaces).
 */
export interface PortalMe {
  user: ProUser | null;
  deals: PortalGrantSummary[];
}

/** What the signer sees when opening a tokenized signing link (§9). */
export interface SignPacket {
  signingRequestId: ID;
  title: string;
  signerName: string;
  status: SigningStatus;
  documentName: string | null;
  /** True when a real file is attached and can be previewed at the token doc URL. */
  hasDocumentFile: boolean;
  /** Advisor-placed signature box on the document, if any (DocuSign-style). */
  signatureField?: SignatureField | null;
  /** The captured signature image (data URL), present once signed. */
  signatureDataUrl: string | null;
  signedAt: ISODateString | null;
  orgName: string;
}

// ── realtime (live updates + presence) ──────────────────────────────────────
// Contract for the Socket.IO layer. The server broadcasts a `DealEvent` to a
// per-deal room after each mutation; the SPA / client portal react by
// refetching (payloads stay deliberately small — id + minimal data only).

/** Mutation kinds broadcast over the deal room. */
export type DealEventName =
  | "step.created"
  | "step.updated"
  | "step.deleted"
  | "edge.created"
  | "edge.deleted"
  | "comment.added"
  | "document.updated"
  | "signing.updated";

/** A live deal event. `payload` is intentionally minimal: the client refetches
 *  the authoritative state, so we ship just enough to scope/debug the change. */
export interface DealEvent {
  dealId: ID;
  event: DealEventName;
  payload?: Record<string, unknown>;
}

/** A participant currently viewing a deal room (for presence avatars). */
export interface PresenceUser {
  /** Stable per-connection id (socket id) — lets the UI de-dupe a user who has
   *  the deal open in two tabs, while still keeping the list keyable. */
  id: ID;
  /** `null` for the unauthenticated demo session; resolved name otherwise. */
  userId: ID | null;
  name: string;
}

// ── canvas co-editing (Yjs / CRDT) ───────────────────────────────────────────
// The deal canvas is a true CRDT shared document (Figma/FigJam model). Both the
// server (seed + debounce-persist) and the client (React Flow binding) agree on
// these shared-type names and the serialized node/edge shapes, so a Y.Doc round-
// trips losslessly through Postgres on cold start. Kept here so server + client
// can never drift. The room name is `deal:<dealId>` (same as the Socket.IO room).

/** Y.Doc shared-type keys for one deal's canvas. */
export const CANVAS_DOC = {
  /** `Y.Map<CanvasNode>` keyed by step id — one entry per node. */
  nodes: "nodes",
  /** `Y.Map<CanvasEdge>` keyed by edge id — one entry per edge. */
  edges: "edges",
} as const;

/** Build the Yjs room name for a deal (also the Socket.IO room name). */
export function dealRoomName(dealId: ID): string {
  return `deal:${dealId}`;
}

/**
 * A canvas node as stored in the shared `Y.Map`. Mirrors `Step` minus the
 * server-managed envelope: just the fields the canvas co-edits live. Stored as
 * a plain JS object value (per-node last-writer-wins, which is conflict-free
 * across *different* nodes — the property we need for two advisors editing the
 * same graph). `note` mirrors `Step.records.note` so side-panel edits round-trip.
 */
export interface CanvasNode {
  id: ID;
  typeKey: StepTypeKey;
  title: string;
  status: StepStatus;
  visibility: Visibility;
  note: string;
  position: Position;
}

/** A canvas edge as stored in the shared `Y.Map`, keyed by edge id. */
export interface CanvasEdge {
  id: ID;
  source: ID;
  target: ID;
}

// ── Billing (Mollie subscriptions; 5 seats incl. + €5/seat overage, §13) ─────

export type SubscriptionStatus = "none" | "trialing" | "active" | "past_due" | "canceled";

/** The three concrete plan tiers (server-defined in PLAN_LIMITS). */
export type PlanKey = "starter" | "pro" | "scale";

/** What a usage limit measures. */
export type LimitKind = "client" | "upload" | "signing";

/**
 * Per-plan usage ceilings. `null` means unlimited (the Scale tier). All counts
 * are measured against the current calendar month except `maxActiveClients`,
 * which is a point-in-time cap on total clients in the org.
 */
export interface PlanLimits {
  /** Total clients allowed to exist at once (point-in-time). */
  maxActiveClients: number | null;
  /** New clients created this calendar month. */
  clientsPerMonth: number | null;
  /** Document uploads this calendar month. */
  uploadsPerMonth: number | null;
  /** Signing requests created this calendar month. */
  signingDocsPerMonth: number | null;
}

/** One row in the plan catalog shown on the billing screen. */
export interface PlanCatalogEntry {
  key: PlanKey;
  name: string;
  /** euros / month, base (includes the first 5 seats) */
  monthly: number;
  includedSeats: number;
  /** euros / month per seat over `includedSeats` */
  perSeatMonthly: number;
  limits: PlanLimits;
}

/** Org's current consumption, keyed to line up with `PlanLimits`. */
export interface UsageCounts {
  /** Clients that currently exist (point-in-time). */
  activeClients: number;
  /** Clients created this calendar month. */
  clientsThisMonth: number;
  /** Document uploads this calendar month. */
  uploadsThisMonth: number;
  /** Signing requests created this calendar month. */
  signingThisMonth: number;
}

export interface Billing {
  plan: string | null;
  status: SubscriptionStatus;
  includedSeats: number;
  activeSeats: number;
  overageSeats: number;
  /** euros / month */
  baseMonthly: number;
  overageMonthly: number;
  totalMonthly: number;
  trialEndsAt: ISODateString | null;
  currentPeriodEnd: ISODateString | null;
  /** Full catalog (Starter / Pro / Scale) so the FE can render plan cards. */
  plans: PlanCatalogEntry[];
  /** Limits of the org's currently-resolved plan (Starter when no paid sub). */
  limits: PlanLimits;
  /** This org's current usage, to render meters against `limits`. */
  usage: UsageCounts;
}
